import type { Student } from '../hooks/useAuth'

/**
 * Avaliação Inteligente Phase 1a — discovery re-take cooldown.
 *
 * Single source of truth for the gating state shared by:
 *   - components/SubjectsBanners.tsx (the /subjects shy banner)
 *   - pages/ProfilePage.tsx          (the Avaliação Inteligente widget)
 *   - any future surface that exposes a re-take CTA
 *
 * Inputs come straight from /auth/me:
 *   - student.last_placement_at        ISO timestamp of latest completed
 *                                      PlacementSession (any subject), or null
 *                                      when no run has finished.
 *   - student.repeat_discovery_days    Tier feature
 *                                      (SubscriptionTierFeature.repeat_discovery_days).
 *                                      0  → re-take anytime (no cooldown)
 *                                      >0 → wait this many days
 *                                      -1 → re-take disabled for this tier
 *                                      undefined → safe default (-1) until
 *                                      /auth/me re-fetches; better to gate
 *                                      than expose a re-take prematurely.
 */
export interface DiscoveryCooldownState {
  /** True when the CTA should be disabled (cooldown OR tier=-1 + completed). */
  blocked: boolean
  /** True when blocked reason is the time-window cooldown (>0 days). */
  inCooldown: boolean
  /** True when the tier never allows re-take and student already completed. */
  retakeForeverDisabled: boolean
  /** Days elapsed since last completed placement (null if none). */
  daysSincePlacement: number | null
  /** Days remaining in the cooldown window (null when not in cooldown). */
  cooldownDaysRemaining: number | null
}

export function computeDiscoveryCooldown(student: Student | null): DiscoveryCooldownState {
  // Default: when /auth/me hasn't loaded yet, fall back to -1 ("disabled")
  // so we never expose the CTA to a free student who just completed one.
  // Once /auth/me resolves, the real tier value takes over.
  const repeatDays = student?.repeat_discovery_days ?? -1
  const lastMs = student?.last_placement_at
    ? new Date(student.last_placement_at).getTime()
    : null
  const daysSince = lastMs !== null
    ? Math.floor((Date.now() - lastMs) / 86_400_000)
    : null

  const inCooldown =
    repeatDays > 0 && daysSince !== null && daysSince < repeatDays
  const cooldownRemaining = inCooldown ? repeatDays - (daysSince ?? 0) : null
  const retakeForeverDisabled = repeatDays === -1 && lastMs !== null

  return {
    blocked:               inCooldown || retakeForeverDisabled,
    inCooldown,
    retakeForeverDisabled,
    daysSincePlacement:    daysSince,
    cooldownDaysRemaining: cooldownRemaining,
  }
}

/**
 * Long-form Portuguese sentence for the locked-CTA tooltip / inline
 * message. Caller decides where to render it (banner body, profile
 * widget, hover tooltip).
 */
export function discoveryCooldownMessage(state: DiscoveryCooldownState): string | null {
  if (state.retakeForeverDisabled) {
    return 'Já fizeste a Avaliação Inteligente e os cursos sugeridos ficam guardados. Activa um dos planos pagos para repetires com outras disciplinas!'
  }
  if (state.inCooldown && state.cooldownDaysRemaining !== null) {
    // Two-beat structure (mobile-first): acknowledge done + nudge to
    // results, then a teacher-tone encouragement to keep practising.
    // The day-countdown lives on the disabled pill next to the CTA, so
    // we don't repeat it in the prose. "quiçá" is intentional — it
    // reads as classroom Portuguese (PT-MZ) and keeps the line warm
    // without over-promising. The 😉 is load-bearing: it signals
    // aspiration, not guarantee. Don't strip it in future edits —
    // we don't yet compute admission probability (Phase 4).
    return 'Já fizeste a Avaliação Inteligente. Vê os cursos sugeridos, continua a praticar e vamos ajudar-te a chegar mais longe. 😉'
  }
  return null
}
